import { useMemo } from "react";
import { dashboardViewCopy } from "../app/i18n";
import { getIntlLocale, type AppLanguage } from "../app/language";
import { priorityClasses } from "../app/data";
import { DashboardStatCard, TicketLastUpdateText } from "../app/ui";
import { formatCount, getInitials, parseTimestampValue, resolveTimeZone } from "../app/utils";
import type { CalendarAssignment, Ticket } from "../app/types";

type DashboardViewProps = {
  assignments: CalendarAssignment[];
  currentUserEmail: string | null;
  currentUserName: string;
  filteredTickets: Ticket[];
  isLoading?: boolean;
  language: AppLanguage;
  onOpenTicket: (ticketId: string) => void;
  timezone: string;
};

const ticketPriorityRank: Record<Ticket["priority"], number> = {
  High: 0,
  Medium: 1,
  Low: 2,
};

const ticketStatusRank: Record<Ticket["status"], number> = {
  Open: 0,
  "In Progress": 1,
  "Waiting customer": 2,
  Pending: 3,
  Resolved: 4,
};

function formatScheduleTimeLabel(value: string, timezone: string, language: AppLanguage) {
  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }

  const resolvedTimeZone = resolveTimeZone(timezone);

  return new Intl.DateTimeFormat(getIntlLocale(language), {
    ...(resolvedTimeZone ? { timeZone: resolvedTimeZone } : {}),
    hour: "numeric",
    minute: "2-digit",
  }).format(parsed);
}

function isAssignmentOnDay(assignment: CalendarAssignment, dayStart: number, dayEnd: number) {
  const assignmentStart = new Date(assignment.startAt).getTime();
  const assignmentEnd = new Date(assignment.endAt).getTime();
  return Number.isFinite(assignmentStart) && Number.isFinite(assignmentEnd) && assignmentStart < dayEnd && assignmentEnd > dayStart;
}

function isTimestampWithinBounds(value: string | null | undefined, dayStart: number, dayEnd: number) {
  const timestamp = parseTimestampValue(value);
  return Number.isFinite(timestamp) && timestamp >= dayStart && timestamp < dayEnd;
}

function LoadingSpinnerBadge({ label }: { label: string }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-[var(--brand-primary-ring)] bg-white/95 px-3 py-2 text-xs font-semibold text-[var(--brand-primary)] shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <span>{label}</span>
    </div>
  );
}

export function DashboardView({
  assignments,
  currentUserEmail,
  currentUserName,
  filteredTickets,
  isLoading = false,
  language,
  onOpenTicket,
  timezone,
}: DashboardViewProps) {
  const copy = dashboardViewCopy[language];
  const accessibleCategoryNames = useMemo(
    () => [...new Set(filteredTickets.map(ticket => ticket.category).filter(Boolean))].sort((left, right) => left.localeCompare(right, getIntlLocale(language), { sensitivity: "base" })),
    [filteredTickets, language],
  );
  const sortedOpenTickets = useMemo(
    () => [...filteredTickets]
      .filter(ticket => ticket.status !== "Resolved")
      .sort((left, right) => {
        const priorityDifference = ticketPriorityRank[left.priority] - ticketPriorityRank[right.priority];

        if (priorityDifference !== 0) {
          return priorityDifference;
        }

        const statusDifference = ticketStatusRank[left.status] - ticketStatusRank[right.status];

        if (statusDifference !== 0) {
          return statusDifference;
        }

        return parseTimestampValue(left.lastUpdate) - parseTimestampValue(right.lastUpdate);
      }),
    [filteredTickets],
  );
  const priorityTickets = useMemo(
    () => sortedOpenTickets.filter(ticket => ticket.priority !== "Low"),
    [sortedOpenTickets],
  );
  const displayedPriorityTickets = priorityTickets.length > 0 ? priorityTickets.slice(0, 4) : sortedOpenTickets.slice(0, 4);
  const todayBounds = useMemo(() => {
    const dayStartDate = new Date();
    dayStartDate.setHours(0, 0, 0, 0);
    const dayEndDate = new Date(dayStartDate);
    dayEndDate.setDate(dayEndDate.getDate() + 1);

    return {
      dayStart: dayStartDate.getTime(),
      dayEnd: dayEndDate.getTime(),
      label: new Intl.DateTimeFormat(getIntlLocale(language), { weekday: "long", month: "short", day: "numeric" }).format(dayStartDate),
    };
  }, [language]);
  const todaysAssignments = useMemo(() => {
    if (!currentUserEmail) {
      return [];
    }

    return assignments
      .filter(assignment => assignment.memberEmail.toLowerCase() === currentUserEmail.toLowerCase())
      .filter(assignment => isAssignmentOnDay(assignment, todayBounds.dayStart, todayBounds.dayEnd))
      .sort((left, right) => new Date(left.startAt).getTime() - new Date(right.startAt).getTime());
  }, [assignments, currentUserEmail, todayBounds.dayEnd, todayBounds.dayStart]);
  const currentTime = Date.now();
  const upcomingAssignment = todaysAssignments.find(assignment => new Date(assignment.startAt).getTime() > currentTime) ?? null;
  const currentUserIdentity = useMemo(
    () => new Set([currentUserName, currentUserEmail].map(value => value?.trim().toLowerCase() ?? "").filter(Boolean)),
    [currentUserEmail, currentUserName],
  );
  const yourActiveTicketsCount = useMemo(
    () => filteredTickets.filter(ticket => {
      if (ticket.status === "Resolved") {
        return false;
      }

      const normalizedAssignee = ticket.assignee?.trim().toLowerCase() ?? "";
      return currentUserIdentity.has(normalizedAssignee);
    }).length,
    [currentUserIdentity, filteredTickets],
  );
  const resolvedTodayCount = useMemo(
    () => filteredTickets.filter(ticket => ticket.status === "Resolved" && isTimestampWithinBounds(ticket.lastUpdate, todayBounds.dayStart, todayBounds.dayEnd)).length,
    [filteredTickets, todayBounds.dayEnd, todayBounds.dayStart],
  );
  const ticketsCreatedTodayCount = useMemo(
    () => filteredTickets.filter(ticket => isTimestampWithinBounds(ticket.createdAt, todayBounds.dayStart, todayBounds.dayEnd)).length,
    [filteredTickets, todayBounds.dayEnd, todayBounds.dayStart],
  );

  return (
    <div className="p-8">
      <section className="mb-8">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[#98a2b3]">{copy.pageEyebrow}</p>
          <h1 className="mt-3 font-headline text-5xl font-extrabold tracking-[-0.06em] text-[#101828]">{copy.pageTitle}</h1>
          <p className="mt-3 max-w-2xl text-sm text-[#667085]">{copy.pageDescription}</p>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-8">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <DashboardStatCard eyebrow={copy.assignedToYou} value={formatCount(yourActiveTicketsCount)} label={copy.yourActiveTickets} accent="bg-[rgba(0,133,93,0.08)] text-[#006949]" isLoading={isLoading} />
            <DashboardStatCard eyebrow={copy.updatedToday} value={formatCount(resolvedTodayCount)} label={copy.resolvedToday} accent="bg-[rgba(102,112,133,0.1)] text-[#344054]" isLoading={isLoading} />
            <DashboardStatCard eyebrow={copy.openedToday} value={formatCount(ticketsCreatedTodayCount)} label={copy.ticketsCreated} accent="bg-[rgba(185,86,82,0.08)] text-[#9a3f3b]" isLoading={isLoading} />
          </div>

          <div className="rounded-[28px] bg-white p-7 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.priorityTicketsTitle}</h2>
                <p className="mt-1 text-sm text-[#667085]">{copy.priorityTicketsDescription}</p>
              </div>
              <div className="rounded-2xl bg-[#f3f3f4] px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#475467]">{copy.inFocus(priorityTickets.length || sortedOpenTickets.length)}</div>
            </div>

            <div className="mb-6 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-[#f8fafc] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#475467]">{copy.categoriesCount(accessibleCategoryNames.length)}</span>
              {accessibleCategoryNames.slice(0, 3).map(categoryName => (
                <span key={categoryName} className="rounded-full bg-[#ecfdf3] px-3 py-1.5 text-[11px] font-semibold text-[#027a48]">{categoryName}</span>
              ))}
              {accessibleCategoryNames.length > 3 ? <span className="rounded-full bg-[#f2f4f7] px-3 py-1.5 text-[11px] font-semibold text-[#475467]">{copy.moreCount(accessibleCategoryNames.length - 3)}</span> : null}
            </div>

            <div className="space-y-3">
              {isLoading ? (
                <div className="flex min-h-[160px] items-center justify-center rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#f8fafc] p-8 text-center">
                  <LoadingSpinnerBadge label={copy.loadingPriorityTickets} />
                </div>
              ) : displayedPriorityTickets.length > 0 ? displayedPriorityTickets.map(ticket => {
                return (
                  <button
                    key={ticket.id}
                    type="button"
                    onClick={() => onOpenTicket(ticket.id)}
                    className="grid w-full gap-4 rounded-[24px] border border-[rgba(0,0,0,0.04)] bg-[#fcfcfd] p-5 text-left transition hover:-translate-y-[1px] hover:border-[rgba(0,105,73,0.2)] hover:shadow-[0_18px_44px_rgba(15,23,42,0.08)] md:grid-cols-[1.5fr_1fr_0.9fr_0.9fr]"
                  >
                    <div>
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="rounded-full bg-[#f2f4f7] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#475467]">{ticket.id}</span>
                        <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${priorityClasses[ticket.priority]}`}>{copy.priorityLabels[ticket.priority]}</span>
                        <span className="rounded-full bg-[#dcfce7] px-3 py-1 text-[11px] font-semibold text-[#166534]">{copy.statusLabels[ticket.status]}</span>
                        <span className="rounded-full bg-[#eef2ff] px-3 py-1 text-[11px] font-semibold text-[#4338ca]">{ticket.category}</span>
                      </div>
                      <h3 className="mt-3 text-base font-semibold text-[#101828]">{ticket.subject}</h3>
                      <p className="mt-2 whitespace-pre-line text-sm text-[#667085]">{ticket.description}</p>
                      <span className="mt-4 inline-flex items-center text-sm font-semibold text-[#006949]">{copy.openDetails}</span>
                    </div>

                    <div className="space-y-2">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.customer}</p>
                      <p className="text-sm font-semibold text-[#101828]">{ticket.customerId}</p>
                      <p className="text-sm text-[#667085]">{copy.customerOnDemand}</p>
                      <TicketLastUpdateText value={ticket.lastUpdate} timezone={timezone} className="text-xs text-[#98a2b3]" />
                    </div>

                    <div className="space-y-2">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.owner}</p>
                      <div className="flex items-center gap-3">
                        <div className="brand-primary-avatar flex h-9 w-9 items-center justify-center rounded-full text-[10px] font-bold">
                          {getInitials(ticket.assignee ?? copy.unassigned)}
                        </div>
                        <p className="text-sm font-semibold text-[#101828]">{ticket.assignee ?? copy.unassigned}</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.plan}</p>
                      <p className="text-sm font-semibold text-[#101828]">{ticket.plan}</p>
                    </div>
                  </button>
                );
              }) : (
                <div className="rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#f8fafc] p-8 text-center">
                  <p className="text-sm font-semibold text-[#101828]">{copy.noPriorityTicketsTitle}</p>
                  <p className="mt-2 text-sm text-[#667085]">{copy.noPriorityTicketsDescription}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6 lg:col-span-4">
          <div className="rounded-[28px] bg-white p-7 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.calendar}</p>
                <h2 className="mt-3 font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.scheduleTitle}</h2>
                <p className="mt-1 text-sm text-[#667085]">{copy.scheduleDescription(currentUserName)}</p>
              </div>
              <div className="min-w-[180px] rounded-[24px] border border-[rgba(0,0,0,0.04)] bg-[#fcfcfd] p-4">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.today}</p>
                <p className="mt-2 text-base font-semibold text-[#101828]">{todayBounds.label}</p>
                <p className="mt-1 text-sm text-[#667085]">{copy.assignmentsScheduled(todaysAssignments.length)}</p>
              </div>
            </div>

            <div className="mt-7 space-y-3">
              {isLoading ? (
                <div className="flex min-h-[120px] items-center justify-center rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#f8fafc] p-8 text-center">
                  <LoadingSpinnerBadge label={copy.loadingSchedule} />
                </div>
              ) : todaysAssignments.length > 0 ? todaysAssignments.map(assignment => {
                const isActive = new Date(assignment.startAt).getTime() <= currentTime && new Date(assignment.endAt).getTime() >= currentTime;
                const isUpcoming = !isActive && upcomingAssignment?.id === assignment.id;

                return (
                  <div key={assignment.id} className="rounded-[24px] border border-[rgba(0,0,0,0.04)] bg-[#fcfcfd] p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${isActive ? "bg-[#dcfce7] text-[#166534]" : isUpcoming ? "bg-[#eef2ff] text-[#4338ca]" : "bg-[#f2f4f7] text-[#475467]"}`}>
                          {isActive ? copy.activeNow : isUpcoming ? copy.upNext : assignment.type}
                        </span>
                        {!isActive && !isUpcoming ? <span className="rounded-full bg-[#f9fafb] px-3 py-1 text-[11px] font-semibold text-[#475467]">{assignment.type}</span> : null}
                      </div>
                      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#98a2b3]">{copy.scheduledAssignment}</span>
                    </div>

                    <h3 className="mt-4 text-base font-semibold text-[#101828]">{assignment.title}</h3>
                    <p className="mt-2 text-sm font-medium text-[#344054]">{formatScheduleTimeLabel(assignment.startAt, timezone, language)} – {formatScheduleTimeLabel(assignment.endAt, timezone, language)}</p>
                    <p className="mt-2 text-sm text-[#667085]">{assignment.notes || copy.noAssignmentNotes}</p>
                  </div>
                );
              }) : (
                <div className="rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#f8fafc] p-8 text-center">
                  <p className="text-sm font-semibold text-[#101828]">{copy.noAssignmentsTitle}</p>
                  <p className="mt-2 text-sm text-[#667085]">{copy.noAssignmentsDescription}</p>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-[28px] bg-white p-7 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.serviceNoticeEyebrow}</p>
              <h2 className="mt-3 font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.serviceNoticeTitle}</h2>
              <p className="mt-1 text-sm text-[#667085]">{copy.serviceNoticeDescription}</p>
            </div>

            <div className="mt-7 flex items-start gap-4 rounded-[24px] border border-[rgba(0,133,93,0.18)] bg-[#ecfdf3] p-5">
              <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-[#12b76a]" />
              <div>
                <p className="text-sm font-semibold text-[#05603a]">{copy.serviceNoticeOperational}</p>
                <p className="mt-1 text-sm text-[#477e63]">{copy.serviceNoticeOperationalDetail}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
